import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Marquee from '../components/Marquee';
import ServicesGrid from '../components/ServicesGrid';
import ProjectShowcase from '../components/ProjectShowcase';
import StatsBand from '../components/StatsBand';
import AboutSection from '../components/AboutSection';
import ProcessSection from '../components/ProcessSection';
import Testimonials from '../components/Testimonials';
import ZonesSection from '../components/ZonesSection';
import FaqSection from '../components/FaqSection';
import LeadForm from '../components/LeadForm';
import CtaBanner from '../components/CtaBanner';
import Footer from '../components/Footer';
import CookiesBanner from '../components/CookiesBanner';
import StickyMobileBar from '../components/StickyMobileBar';
import { useReveal } from '../hooks/useReveal';
import { FAQS } from '../lib/site';

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
  const progressRef = useRef<HTMLDivElement>(null);
  useReveal();

  // Barra de progreso de lectura
  useEffect(() => {
    const st = ScrollTrigger.create({
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        if (progressRef.current) {
          gsap.to(progressRef.current, {
            scaleX: self.progress,
            duration: 0.12,
            ease: 'none',
            overwrite: 'auto',
          });
        }
      },
    });
    return () => st.kill();
  }, []);

  return (
    <div id="top" className="relative min-h-screen bg-paper text-ink grain">
      <SEO
        title="Construcción, Reformas y Mantenimiento en Málaga"
        description="Empresa de construcción y reformas integrales en Málaga. +15 años, +150 proyectos entregados, garantía de 10 años por escrito y presupuesto gratis en 24h. Obra nueva, reformas de viviendas y locales comerciales en la Costa del Sol."
        url="/"
        breadcrumbs={[{ name: 'Inicio', url: '/' }]}
        faqSchema={FAQS.slice(0, 6)}
      />

      <div
        ref={progressRef}
        className="fixed top-0 left-0 right-0 h-[3px] bg-clay origin-left z-[92] scale-x-0"
        aria-hidden="true"
      />

      <Header />
      <main>
        <Hero />
        <Marquee />
        <ServicesGrid />
        <ProjectShowcase />
        <StatsBand />
        <AboutSection />
        <ProcessSection />
        <Testimonials />
        <ZonesSection />
        <FaqSection limit={5} />

        {/* Zona de captación final */}
        <section id="presupuesto" className="py-24 md:py-32 bg-paper-2 border-y border-line">
          <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 lg:gap-20 items-center">
            <div className="reveal">
              <p className="label">Presupuesto gratis</p>
              <h2 className="font-display font-black uppercase tracking-[-0.02em] text-section-title mt-5">
                Cuéntanos tu proyecto, <em className="font-serifit normal-case font-normal text-clay">te damos cifras</em>
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed max-w-lg">
                Detallamos cada partida para que sepas exactamente qué pagas:
                materiales, mano de obra, gestión de residuos y plazos.
                Sin coste, sin permanencia y sin letra pequeña.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  'Visita y medición en tu vivienda o local',
                  'Presupuesto cerrado en 24–48h',
                  'Plazos por escrito con compromiso real',
                  'Garantía de 10 años en el contrato',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-clay text-white text-[0.65rem] font-bold shrink-0 mt-px">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal">
              <LeadForm />
            </div>
          </div>
        </section>

        <CtaBanner />
      </main>
      <Footer />
      <StickyMobileBar />
      <CookiesBanner />
    </div>
  );
};

export default HomePage;
